import React, { useMemo } from 'react';
import AnalysisCard from '../AnalysisCard';
import useLanguage from '@op/hooks/useLanguage';
import { reviewsFormat } from '@op/utils/value';
import PercentageItem from '@op/components/Snippets/PercentageItem';
import { useSummaryKeywordDrillStats } from '../../hook';
import { getRationPercentValue } from '@op/utils';
import { formatDateFunc } from '@op/utils/time';
import BubbleCommon, {
  keywordDrillUrlFilteredReviewsEngagementTips,
  keywordDrillUrlFilteredReviewsMentionsTips,
  keywordDrillUrlFilteredReviewsTips,
} from '@op/components/BubbleCommon';
import { DBO_TM, DBO_V } from '@op/utils/format';

export default function TotalSummaryMetricsCard(props: any) {
  const { filter, tab, gameInfo } = props;
  const { formatMessage } = useLanguage();

  const params = useMemo(
    () => ({
      ...filter,
      tab_type: 'all',
      need_link: true,
      is_today: DBO_TM.isToday(filter),
      channel_type: tab !== 'total' ? 'social' : '',
    }),
    [filter, tab],
  );

  const { data, loading, error } = useSummaryKeywordDrillStats({
    filter: params,
    gameInfo,
  });

  const {
    mentions,
    totalMentions,
    oldMentionsRatio,
    positiveMentions,
    negativeMentions,
    mentionsRatio,
  } = useMemo(() => {
    const {
      positive_mentions,
      negative_mentions,
      filtered_reviews,
      total_reviews,
      filtered_reviews_old,
      total_reviews_old,
    } = data || {};
    const positive = DBO_V.abs(positive_mentions);
    const negative =  DBO_V.abs(negative_mentions);

    // 过滤声量
    const mentions = DBO_V.abs(filtered_reviews);
    // 总声量
    const totalMentions = DBO_V.abs(total_reviews);

    // 旧过滤声量
    const oldMentions = DBO_V.abs(filtered_reviews_old);
    // 旧总声量
    const oldTotalMentions = DBO_V.abs(total_reviews_old);

    const newMentionsRatio = (mentions / totalMentions) * 100;
    const oldMentionsRatio = (oldMentions / oldTotalMentions) * 100;

    return {
      mentions: newMentionsRatio,
      totalMentions,
      oldMentionsRatio,
      positiveMentions: (positive / mentions) * 100,
      negativeMentions: (negative / mentions) * 100,
      mentionsRatio: getRationPercentValue(
        newMentionsRatio,
        oldMentionsRatio,
        6,
      ),
    };
  }, [data]);

  function footerFormat(value: number) {
    return DBO_V.ratio(value, '-', 1);
  }

  return (
    <>
      {/* Filtered Reviews */}
      <AnalysisCard
        value={data?.filtered_reviews || 0}
        title={
          <BubbleCommon
            showIcon={false}
            tipsMap={keywordDrillUrlFilteredReviewsTips}
          >
            {formatMessage({ id: 'Filtered Reviews' })}
          </BubbleCommon>
        }
        percentValue={data?.filtered_reviews_ratio || 0}
        footer={formatMessage(
          { id: 'out of {total} Reviews' },
          {
            total: (
              <span className="footer-value-text">
                {reviewsFormat(data?.total_reviews || 0)}
              </span>
            ),
          },
        )}
        loading={loading}
        loadingHeight={57}
        // empty={false}
        bubbleProps={{
          showContent: !DBO_V.is_empty(data?.filtered_reviews),
          oldTime: [data?.link_of_start_time, data?.link_of_end_time],
          newTime: [data?.start_time, data?.end_time],
          oldValue: data?.filtered_reviews_old,
          newValue: data?.filtered_reviews,
          formateDate: formatDateFunc(filter?.date_type || 'daily'),
          formateValue: reviewsFormat,
        }}
      />

      {/* Mentions% */}
      <AnalysisCard
        value={mentions}
        title={
          <BubbleCommon
            showIcon={false}
            tipsMap={keywordDrillUrlFilteredReviewsMentionsTips}
          >
            {formatMessage({ id: 'Mentions%' })}
          </BubbleCommon>
        }
        percentValue={mentionsRatio || 0}
        formatValue={(value) => DBO_V.ratio_min(value / 100)}
        footer={
          <div className="dbo-analysis-mention-card">
            <>
              {formatMessage({ id: 'Positive%' })}
              <PercentageItem
                percentage={positiveMentions || 0}
                showIcon={false}
                formateFunc={footerFormat}
                className="success"
              />
            </>
            <>
              {formatMessage({ id: 'Negative%' })}
              <PercentageItem
                percentage={negativeMentions || 0}
                showIcon={false}
                formateFunc={footerFormat}
                className="error"
              />
            </>
          </div>
        }
        loading={loading}
        loadingHeight={57}
        // empty={error || data?.empty}
        bubbleProps={{
          showContent: !DBO_V.is_empty(totalMentions),
          oldTime: [data?.link_of_start_time, data?.link_of_end_time],
          newTime: [data?.start_time, data?.end_time],
          oldValue: oldMentionsRatio,
          newValue: mentions,
          formateDate: formatDateFunc(filter?.date_type || 'daily'),
          formateValue: (value: number) => DBO_V.ratio_min_pro(value / 100),
        }}
      />

      {/* Engagements */}
      <AnalysisCard
        value={data?.filtered_engagements || 0}
        title={
          <BubbleCommon
            showIcon={false}
            tipsMap={keywordDrillUrlFilteredReviewsEngagementTips}
          >
            {formatMessage({ id: 'Engagement' })}
          </BubbleCommon>
        }
        percentValue={data?.filtered_engagements_ratio || 0}
        footer={formatMessage(
          { id: 'out of {total} Engagement' },
          {
            total: (
              <span className="footer-value-text">
                {reviewsFormat(data?.total_engagements || 0)}
              </span>
            ),
          },
        )}
        loading={loading}
        loadingHeight={57}
        // empty={error || data?.empty}
        bubbleProps={{
          showContent: !DBO_V.is_empty(data?.filtered_engagements),
          oldTime: [data?.link_of_start_time, data?.link_of_end_time],
          newTime: [data?.start_time, data?.end_time],
          oldValue: data?.filtered_engagements_old,
          newValue: data?.filtered_engagements,
          formateDate: formatDateFunc(filter?.date_type || 'daily'),
          formateValue: reviewsFormat,
        }}
      ></AnalysisCard>
    </>
  );
}
